import { useEffect, useState } from 'react';
import type { RealPhoto } from '@/types/piclick';

type Props = {
  photos: RealPhoto[];
};

export function RealPhotosGallery({ photos }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIndex(null);
      if (e.key === 'ArrowRight') {
        setOpenIndex((i) => (i === null ? null : Math.min(photos.length - 1, i + 1)));
      }
      if (e.key === 'ArrowLeft') {
        setOpenIndex((i) => (i === null ? null : Math.max(0, i - 1)));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openIndex, photos.length]);

  if (photos.length === 0) {
    return null;
  }

  const openPhoto = openIndex !== null ? photos[openIndex] : null;

  return (
    <section className="mt-14 pb-10 sm:mt-20 sm:pb-14">
      <header className="mb-6 px-1 text-center sm:mb-8">
        <span className="inline-block rounded-full border-[3px] border-plum bg-butter px-4 py-1.5 font-action text-[0.8rem] font-bold uppercase tracking-wider text-plum shadow-[0_3px_0_0_#600b2e]">
          Fotos reais
        </span>
        <h2
          className="font-action text-2xl font-bold leading-tight text-plum sm:text-3xl"
          style={{ marginTop: '1.25rem' }}
        >
          Como fica na vida real
        </h2>
        <p
          className="font-action text-sm text-plum/70"
          style={{ marginTop: '0.5rem' }}
        >
          Inspirações de Piclicks já produzidos. Toque pra ampliar.
        </p>
      </header>

      <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setOpenIndex(i)}
            className="group relative flex-none snap-start overflow-hidden rounded-[1.5rem] border-[3px] border-plum bg-white shadow-[0_5px_0_0_#600b2e] transition-transform active:translate-y-[2px] active:shadow-[0_2px_0_0_#600b2e]"
            style={{ width: '70vw', maxWidth: '280px' }}
            aria-label={`Ver foto ${i + 1} de ${photos.length}`}
          >
            <div
              className="flex items-center justify-center overflow-hidden bg-cream"
              style={{ aspectRatio: '1 / 1' }}
            >
              <img
                src={photo.imageUrl}
                alt={photo.alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                style={{ objectPosition: '50% 50%' }}
              />
            </div>
            {photo.caption && (
              <div className="px-3 py-2 text-left text-xs font-action font-bold text-plum">
                {photo.caption}
              </div>
            )}
          </button>
        ))}
      </div>

      {openPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Foto ampliada"
          className="fixed inset-0 z-50 flex items-center justify-center bg-plum/80 p-4"
          onClick={() => setOpenIndex(null)}
        >
          <div
            className="relative w-full max-w-2xl rounded-[1.75rem] border-[3px] border-plum bg-white shadow-[0_8px_0_0_#600b2e] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(null)}
              aria-label="Fechar"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-plum bg-white text-plum shadow-[0_3px_0_0_#600b2e] active:translate-y-[1px] active:shadow-[0_1px_0_0_#600b2e]"
            >
              ✕
            </button>
            <img
              src={openPhoto.imageUrl}
              alt={openPhoto.alt}
              className="w-full"
            />
            {openPhoto.caption && (
              <p className="px-5 py-3 font-action text-sm text-plum">
                {openPhoto.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
